import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CRMApiAdapter } from '../adapters/CRMApiAdapter';
import { CrmStatusApiAdapter } from '../adapters/CrmStatusApiAdapter';
import { TagsApiAdapter } from '../adapters/TagsApiAdapter';

const invalidateCRMQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['crm-orders'] });
  queryClient.invalidateQueries({ queryKey: ['crm-orders-count-by-status'] });
};

const runParallel = async <T>(items: T[], fn: (item: T) => Promise<void>): Promise<number> => {
  const results = await Promise.allSettled(items.map(fn));
  return results.filter((r) => r.status === 'rejected').length;
};

export const useCancelOrdersBulk = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new CRMApiAdapter(httpClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderIds: number[]) => apiAdapter.cancelOrdersBulk(orderIds),
    onSettled: () => invalidateCRMQueries(queryClient),
  });
};

export const useConfirmOrdersBulk = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new CRMApiAdapter(httpClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderIds: number[]) => apiAdapter.confirmOrdersBulk(orderIds),
    onSettled: () => invalidateCRMQueries(queryClient),
  });
};

export const useUncancelOrdersBulk = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new CRMApiAdapter(httpClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderIds: number[]) => apiAdapter.uncancelOrdersBulk(orderIds),
    onSettled: () => invalidateCRMQueries(queryClient),
  });
};

export const useModifyOrderStatusBulk = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new CrmStatusApiAdapter(httpClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderIds, crmStatusId }: { orderIds: number[]; crmStatusId: number | null }) => {
      const failed = await runParallel(orderIds, (id) =>
        apiAdapter.modifyOrderStatus({ order_id: id, crm_status_id: crmStatusId }),
      );
      if (failed > 0) throw new Error(`${failed} pedidos no pudieron actualizarse`);
    },
    onSettled: () => invalidateCRMQueries(queryClient),
  });
};

export const useAddTagsToOrdersBulk = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new TagsApiAdapter(httpClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderIds, tagIds }: { orderIds: number[]; tagIds: number[] }) => {
      const failed = await runParallel(orderIds, (id) =>
        apiAdapter.modifyOrderTags({ order_id: id, attach_tags: tagIds }),
      );
      if (failed > 0) throw new Error(`${failed} pedidos no pudieron actualizarse`);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-orders'] });
      queryClient.invalidateQueries({ queryKey: ['crm-tags'] });
    },
  });
};
